import { TeamModeClient } from "~/api/teamMode";
import { ApiClientError } from "~/api/apiClient";
import type { QueryKey } from "@tanstack/vue-query";

export const teamModeMutations = () => {
  const queryClient = useQueryClient();
  const api = useApiClient(TeamModeClient);
  const resolvedQueries = queries();
  const teamModeQueries = resolvedQueries.teamMode;
  const { invalidateAllEntries } = entriesMutations();

  const toast = useToast();
  const throwWithErrorToast = (err: unknown): never => {
    if (err instanceof ApiClientError) {
      toast.error(err.message);
    }
    throw err;
  };

  const shareSchemaMutation = useMutation({
    mutationFn: api.shareSchema,
    onSuccess: (schema, variables) => {
      if (variables.channelIds.length === 0) {
        toast.success("Das Schema wurde erfolgreich offline genommen.");
      } else {
        toast.success("Änderungen am Online-Status wurden gespeichert.");
      }
      const invalidationKeys: QueryKey[] = [
        teamModeQueries._def,
        ...variables.channelIds.map(
          (id) => teamModeQueries.channelInfo(id).queryKey
        ),
      ];

      void invalidateKeys(queryClient, invalidationKeys);
      invalidateAllEntries(schema);
    },
    onError: (err) => throwWithErrorToast(err),
  });

  const subscribeChannelMutation = useMutation({
    mutationFn: api.subscribeChannel,
    onSuccess: () => {
      toast.success("Abonnement erfolgreich erstellt.");
      void queryClient.invalidateQueries();
    },
  });

  const createChannelMutation = useMutation({
    mutationFn: api.createChannel,
    onSuccess: () => {
      toast.success("Kanal erfolgreich erstellt.");
      const invalidationKeys = [teamModeQueries._def];
      void invalidateKeys(queryClient, invalidationKeys);
    },
    onError: (err) => throwWithErrorToast(err),
  });

  const deleteChannelMutation = useMutation({
    mutationFn: api.deleteChannel,
    onSuccess: () => {
      toast.success("Kanal wurde erfolgreich gelöscht.");
      void queryClient.invalidateQueries();
    },
    onError: (err) => throwWithErrorToast(err),
  });

  const detachSchemaFromChannelMutation = useMutation({
    mutationFn: api.detachSchemaFromChannel,
    onSuccess: () => {
      toast.success("Das Schema wurde erfolgreich getrennt.");
      void queryClient.invalidateQueries();
    },
    onError: (err) => throwWithErrorToast(err),
  });

  const deleteChannelSubscriptionMutation = useMutation({
    mutationFn: api.deleteChannelSubscription,
    onSuccess: () => {
      toast.success("Abmeldung erfolgreich.");
      void queryClient.invalidateQueries();
    },
    onError: (err) => throwWithErrorToast(err),
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: api.updateSubscription,
    onSuccess: () => {
      toast.success("Aktualisierung erfolgreich.");
      void queryClient.invalidateQueries();
    },
    onError: (err) => throwWithErrorToast(err),
  });

  const updateChannelMutation = useMutation({
    mutationFn: api.updateChannel,
    onSuccess: (result) => {
      toast.success("Kanal wurde erfolgreich aktualisiert.");
      void queryClient.invalidateQueries();

      queryClient.setQueryData(
        teamModeQueries.channelInfo(result.id).queryKey,
        result
      );
    },
    onError: (err) => throwWithErrorToast(err),
  });

  return {
    shareSchemaMutation,
    subscribeChannelMutation,
    createChannelMutation,
    deleteChannelMutation,
    detachSchemaFromChannelMutation,
    deleteChannelSubscriptionMutation,
    updateSubscriptionMutation,
    updateChannelMutation,
  };
};
